import React, { useState, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';

const MergePDF = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [merging, setMerging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
      setSelectedFiles(prevFiles => [...prevFiles, ...pdfFiles]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setShowPreview(false);
    }
  };

  const reorderFiles = (fromIndex: number, toIndex: number) => {
    setSelectedFiles(files => {
      const newFiles = [...files];
      const [movedFile] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, movedFile);
      return newFiles;
    });
  };

  const mergePDFs = async () => {
    if (selectedFiles.length === 0) return;

    setMerging(true);
    setProgress(0);

    try {
      // Create a new PDF document
      const mergedPdf = await PDFDocument.create();
      const totalPages = selectedFiles.length;
      let processedPages = 0;

      // Process each PDF file
      for (const file of selectedFiles) {
        try {
          // Read the PDF file
          const fileArrayBuffer = await file.arrayBuffer();
          const pdf = await PDFDocument.load(fileArrayBuffer);
          
          // Copy all pages from the current PDF to the merged PDF
          const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          copiedPages.forEach((page: any) => {
            mergedPdf.addPage(page);
          });

          // Update progress
          processedPages++;
          setProgress((processedPages / totalPages) * 100);
        } catch (error) {
          console.error(`Error processing PDF ${file.name}:`, error);
        }
      }

      // Save the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      const mergedPdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      
      // Create preview URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      const previewUrl = URL.createObjectURL(mergedPdfBlob);
      setPdfPreviewUrl(previewUrl);
      setShowPreview(true);
    } catch (error) {
      console.error('Error merging PDFs:', error);
    } finally {
      setMerging(false);
    }
  };

  const handleDownload = () => {
    if (!pdfPreviewUrl) return;

    const link = document.createElement('a');
    link.href = pdfPreviewUrl;
    link.download = 'merged-document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-montserrat font-semibold mb-4">Merge PDFs</h2>
      
      <div className="space-y-6">
        <div className="border-2 border-dashed border-cyan-200 hover:border-cyan-300 transition-colors rounded-xl p-6 text-center bg-gradient-to-b from-cyan-50 to-white">
          <input
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="pdf-input"
          />
          <label
            htmlFor="pdf-input"
            className="cursor-pointer inline-flex items-center justify-center px-6 py-3 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-all transform hover:scale-105 font-semibold shadow-md hover:shadow-lg"
          >
            Add PDFs
          </label>
          <p className="mt-2 text-sm text-gray-600">
            {selectedFiles.length > 0
              ? `${selectedFiles.length} PDF${selectedFiles.length > 1 ? 's' : ''} selected`
              : 'Select multiple PDF files to merge'}
          </p>
        </div>

        {selectedFiles.length > 0 && (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold mb-3">Selected Files</h3>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200 hover:border-cyan-200 transition-colors shadow-sm hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 text-sm">{index + 1}</span>
                      <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {index > 0 && (
                        <button
                          onClick={() => reorderFiles(index, index - 1)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ↑
                        </button>
                      )}
                      {index < selectedFiles.length - 1 && (
                        <button
                          onClick={() => reorderFiles(index, index + 1)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ↓
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-600 transition-colors transform hover:scale-110 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={mergePDFs}
                disabled={merging}
                className="flex-1 px-6 py-3 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-all transform hover:scale-105 disabled:bg-gray-400 disabled:hover:scale-100 font-semibold shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
              >
                {merging ? 'Merging...' : 'Merge PDFs'}
              </button>

              {pdfPreviewUrl && (
                <button
                  onClick={handleDownload}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all transform hover:scale-105 font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  Download
                </button>
              )}
            </div>

            {merging && (
              <div className="w-full bg-gray-100 rounded-full h-3 shadow-inner">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-3 rounded-full transition-all duration-300 shadow-sm"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {showPreview && pdfPreviewUrl && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">PDF Preview</h3>
            <div className="w-full h-[800px] border border-gray-200 rounded-xl shadow-lg overflow-hidden bg-gray-50">
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-full rounded-xl"
                title="PDF Preview"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MergePDF;
